import { endDateOf } from "features/billing-period/contract-term-field";
import { fromMonthInput } from "features/billing-period/billing-period";
import { Product, ProductGroupItem } from "types/Product";
import { BillingSettings } from "./invoices-section";

type OrderPayloadInput = {
  /** Set when editing. */
  orderId: string | undefined;
  companyId: string | undefined;
  customerId: string;
  date: string;
  description: string;
  responsibleUser: string;
  /** Complete lines only — what the user is saving. */
  orderItems: Product[];
  productGroups: ProductGroupItem[];
  billing: BillingSettings;
  /** The order's invoice settings are frozen, see below. */
  isClosed: boolean;
  /** The end date as the server has it; "" when there is none. */
  loadedContractEndDate: string;
  isExternal: boolean;
  externalNumber: string;
};

function sumOfLines(lines: { quantity: number | ""; price: number | "" }[]) {
  let sum = 0;
  for (const line of lines) {
    const quantity = parseFloat(String(line.quantity)) || 0;
    const price = parseFloat(String(line.price)) || 0;
    sum += quantity * price;
  }
  return sum;
}

/** The body `orders.service` post / patch send for the order form. */
export function buildOrderPayload(form: OrderPayloadInput) {
  const contractEndDate = endDateOf(form.billing.term);
  const { billing } = form;

  return {
    id: form.orderId,
    description: form.description,
    date: form.date,
    customer: form.customerId,
    orderItems: form.orderItems,
    productGroups: form.productGroups,
    responsibleUser: form.responsibleUser,
    company: form.companyId,
    totalPrice: sumOfLines(form.orderItems) + sumOfLines(form.productGroups),
    // A closed order's invoice settings are frozen server-side, and ANY of
    // these keys being present — even holding the value it already has —
    // trips that freeze (orders/patch.js's `touchesInvoiceSettings`). The form
    // disables the fields of a closed order, so leaving the keys out here is
    // "send nothing that touches invoice settings" and lets the rest (description,
    // items, responsible user, …) still save.
    ...(form.isClosed
      ? {}
      : {
          isRecurring: billing.isRecurring,
          // On an edit, only when changed — an untouched end date stays
          // exactly as the server has it rather than round-tripping
          // through the browser. Null is an indefinite contract.
          ...(!form.orderId || contractEndDate !== form.loadedContractEndDate
            ? {
                contractEndDate:
                  billing.isRecurring && contractEndDate ? contractEndDate : null,
              }
            : {}),
          invoiceScheduleMode: billing.isRecurring
            ? billing.scheduleMode
            : "last_day_of_month",
          invoiceScheduleDay:
            billing.isRecurring && billing.scheduleMode === "specific_day"
              ? parseInt(billing.scheduleDay, 10)
              : null,
          billingPeriodMode: billing.isRecurring ? billing.periodMode : null,
          billingStartDate: billing.isRecurring
            ? fromMonthInput(billing.startMonth)
            : null,
        }),
    origin: form.isExternal ? "external" : "erp",
    externalNumber: form.isExternal ? form.externalNumber.trim() : null,
  };
}
